/* LEGEND — the Volume Pulse sub-pane and its readout (rules + state: lib/vol-pulse.ts PulseModel).
   Draws the model: the closed bars as one solid baseline line around the median, the forming bar as a dashed segment
   to its pace, a dot on each climax spike; the readout is pinned to the top-left of the pane. */
import React from 'react';
import { BaselineSeries, LineSeries, LineStyle, createSeriesMarkers } from 'lightweight-charts';
import { RV_LO, RV_HI, PulseModel, rvTier, fmtX, type FormingOf, type PulseRead } from '../lib/vol-pulse';
import { fmtSharesShort } from '../lib/format';

const { useEffect, useRef, useState } = React;
const h = React.createElement;

/** The readout's feed: VolPulse emits, VolPulseReadout renders — a print never re-renders ChartPane. */
export class ReadBus {
  private fns = new Set<(r: PulseRead | null) => void>();
  last: PulseRead | null = null;
  emit = (r: PulseRead | null): void => { this.last = r; this.fns.forEach((f) => f(r)); };
  on(f: (r: PulseRead | null) => void): () => void { this.fns.add(f); return () => { this.fns.delete(f); }; }
}

const AMBER = '#e0b15a';
const HOT = '#ff7a45';
const EMIT_MS = 150;     // a fast tape re-reads the forming bar on every print; the readout needs a few frames a second
let nextId = 1;

export class VolPulse {
  readonly id = nextId++;
  readonly rv: any;
  readonly pace: any;
  readonly model: PulseModel;
  private marks: any;
  private paceKey = '';
  private margins = { top: 0.3, bottom: 0.04 };
  private emitTimer: ReturnType<typeof setTimeout> | null = null;
  private gone = false;

  constructor(chart: any, pane: number, bars: () => { time: number; value: number }[], formingOf: FormingOf,
    private readonly emit: (r: PulseRead | null) => void) {
    this.model = new PulseModel(bars, formingOf);
    const scale = {
      priceScaleId: '', lastValueVisible: false, priceLineVisible: false,
      priceFormat: { type: 'custom', minMove: 0.01, formatter: (v: number) => fmtX(Math.pow(2, v)) },
      autoscaleInfoProvider: () => ({ priceRange: { minValue: RV_LO, maxValue: RV_HI } }),
    };
    this.rv = chart.addSeries(BaselineSeries, {
      ...scale,
      baseValue: { type: 'price', price: 0 }, lineWidth: 2, crosshairMarkerRadius: 3,
      topLineColor: AMBER, topFillColor1: 'rgba(224,177,90,0.34)', topFillColor2: 'rgba(224,177,90,0.05)',
      bottomLineColor: 'rgba(140,148,160,0.6)', bottomFillColor1: 'rgba(140,148,160,0.03)', bottomFillColor2: 'rgba(140,148,160,0.12)',
    }, pane);
    this.rv.priceScale().applyOptions({ scaleMargins: this.margins });
    // Axis tags on 1× and 4× only: in a pane this short a 2× tag overlaps both, and a 'median' title sat on the newest bars.
    try {
      this.rv.createPriceLine({ price: 0, color: 'rgba(200,206,214,0.6)', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: '' });
      this.rv.createPriceLine({ price: 1, color: 'rgba(224,177,90,0.35)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false, title: '' });
      this.rv.createPriceLine({ price: 2, color: 'rgba(255,122,69,0.32)', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: '' });
    } catch (e) { /* cosmetic */ }
    // the forming bar: dashed, from the last closed bar to where its volume lands at the close
    this.pace = chart.addSeries(LineSeries, {
      ...scale, color: 'rgba(224,177,90,0.9)', lineWidth: 2, lineStyle: LineStyle.Dashed,
      crosshairMarkerVisible: false, pointMarkersVisible: true, pointMarkersRadius: 3,
    }, pane);
    this.marks = createSeriesMarkers(this.rv, []);
  }

  paneIndex(): number { return this.rv.getPane().paneIndex(); }

  /** Re-read the bars: LiveCandles' Touch (`times`, `reset`), a MOCK tick, or the 1 s clock (no arguments). */
  update(times: readonly number[] = [], reset = false): void {
    if (this.gone) return;
    const m = this.model;
    if (m.update(times, reset)) {
      this.rv.setData(m.solid());
      // dots only: in a pane this small the multiples' text piled into each other and the readout; hover reads the number
      try {
        this.marks.setMarkers(m.spikes().map((s) => ({ time: s.time, position: 'inBar', shape: 'circle', size: 0.4, color: HOT })));
      } catch (e) { /* series gone mid-rebuild */ }
      this.paceKey = '';
    }
    const pts = m.pace();
    const key = pts.map((p) => p.time).join(':') || 'none';
    if (key !== this.paceKey) { this.pace.setData(pts); this.paceKey = key; }
    else if (pts.length) this.pace.update(pts[pts.length - 1]);
    if (!this.emitTimer) {
      this.emitTimer = setTimeout(() => { this.emitTimer = null; if (!this.gone) this.emit(this.model.read()); }, EMIT_MS);
    }
  }

  /** The crosshair's bar time, or null when it leaves the chart. */
  hover(time: number | null): void {
    if (this.gone || time === this.model.hoverTime) return;
    this.model.hoverTime = time;
    this.emit(this.model.read());
  }

  /** Keep the line clear of the readout (top) and of the verdict ribbon (bottom) — fractions of the pane. */
  setMargins(top: number, bottom: number): void {
    if (this.gone || (Math.abs(top - this.margins.top) < 0.01 && Math.abs(bottom - this.margins.bottom) < 0.01)) return;
    this.margins = { top, bottom };
    this.rv.priceScale().applyOptions({ scaleMargins: this.margins });
  }

  /** Before the chart removes the series (ChartPane's rebuild teardown). */
  dispose(): void {
    this.gone = true;
    if (this.emitTimer) clearTimeout(this.emitTimer);
    this.emit(null);
  }
}

const LABEL = { bar: 'Bar vol', pace: 'Vol pace', now: 'Vol now' };

/** The readout: pinned 2px under the top of the pulse pane, wherever the pane separators sit. Memoized: its props are
 *  refs, so a ChartPane re-render (every crosshair move re-renders the Terminal) never reaches it. */
export const VolPulseReadout = React.memo(function VolPulseReadout({ bus, hostRef, chartRef, pulseRef }: any) {
  const [r, setR] = useState<PulseRead | null>(bus.last);
  const elRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => bus.on(setR), [bus]);

  // Placement follows the pane heights (the model's, no layout read) and re-measures the DOM only when one changes;
  // the verdict ribbon over the chart bottom is looked up a few times a second.
  useEffect(() => {
    let raf = 0, sig = '', frame = 0, ribbon = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const el = elRef.current, chart = chartRef.current, host = hostRef.current, p: VolPulse | null = pulseRef.current;
      if (!el || !chart || !host || !p || el.classList.contains('off')) { sig = ''; return; }
      if (frame++ % 20 === 0) {
        const rib = host.parentElement ? host.parentElement.querySelector('.vr-ribbon') as HTMLElement | null : null;
        ribbon = rib ? rib.offsetHeight : 0;
      }
      try {
        const panes = chart.panes();
        const idx = p.paneIndex();
        const pane = panes[idx];
        if (!pane) return;
        const s = p.id + '|' + idx + '|' + ribbon + '|' + panes.map((q: any) => q.getHeight()).join(',');
        if (s === sig) return;
        const pe = pane.getHTMLElement();
        if (!pe) return;
        sig = s;
        const top = pe.getBoundingClientRect().top - host.getBoundingClientRect().top + host.offsetTop;
        el.style.top = Math.round(top + 2) + 'px';
        el.style.visibility = 'visible';
        const ph = Math.max(1, pane.getHeight());
        p.setMargins(clamp((el.offsetHeight + 8) / ph, 0.15, 0.5), ribbon ? clamp((ribbon + 4) / ph, 0.04, 0.35) : 0.04);
      } catch (e) { /* chart mid-rebuild */ }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!r) return h('div', { className: 'rv-read off', ref: elRef, style: { visibility: 'hidden' } });
  const t = rvTier(r.x);
  const kids: any[] = [
    h('span', { key: 'k', className: 'rv-k' }, LABEL[r.mode]),
    h('span', { key: 'x', className: 'rv-x t-' + t.key }, fmtX(r.x)),
    h('span', { key: 't', className: 'rv-tier t-' + t.key }, t.label),
    h('span', { key: 'r', className: 'rv-raw' },
      h('b', null, fmtSharesShort(r.vol)), h('i', null, r.forming ? ' so far · median ' : ' vs median '), fmtSharesShort(Math.round(r.med))),
  ];
  if (r.mode !== 'bar' && r.streak) {
    const s = r.streak;
    kids.push(h('span', { key: 's', className: 'rv-streak' + (s.above ? ' on' : '') },
      r.up != null ? h('i', { className: 'rv-arrow ' + (r.up ? 'up' : 'dn') }, r.up ? '▲' : '▼') : null,
      (r.forming ? 'prev ' : '') + s.n + (s.n === 1 ? ' bar ' : ' bars ') + (s.above ? 'above' : 'below') + ' median'));
  }
  return h('div', { className: 'rv-read', ref: elRef, style: { visibility: 'hidden' } }, kids);
});

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
